import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  useMediaQuery,
  Pagination,
} from "@mui/material";
import {
  Delete,
  Visibility,
  Add as AddIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useDelete from "../../../../../api/useDelete";
import { useAuth } from "../../../../../context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const ListReturnToCompany = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  // Pagination & search
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const { data, refetch, isLoading, isError } = useFetchObjects(
    ["returns"],
    `return-to-company/?search=${encodeURIComponent(
      search
    )}&page=${page}&page_size=${perPage}`,
    token
  );

  useEffect(() => {
    if (data?.count != null) setTotal(data.count);
  }, [data]);

  useEffect(() => {
    refetch();
  }, [search, page, perPage]);

  const { handleDelete, ConfirmDialog } = useDelete("return-to-company", token);

  // Dialog
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const openDetail = (r) => {
    setSelected(r);
    setOpen(true);
  };
  const closeDetail = () => setOpen(false);

  const rows = data?.results || [];

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ p: 3, m: 2, borderRadius: 2 }} elevation={2}>
        <Grid container alignItems="center" spacing={2} mb={2}>
          <Grid item xs>
            <Typography variant="h5">{t("pharmacy.returnList")}</Typography>
          </Grid>
          <Grid item>
            <Button
              startIcon={<AddIcon />}
              onClick={() => navigate("/pharmacy/return-to-company/add")}
              variant="contained"
            >
              {t("pharmacy.addNew")}
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("pharmacy.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Divider sx={{ mb: 2 }} />

        {isLoading ? (
          <Box textAlign="center">
            <CircularProgress />
          </Box>
        ) : isError ? (
          <Typography color="error">{t("pharmacy.loadFailed")}</Typography>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: theme.palette.grey[100] }}>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t("pharmacy.date")}</TableCell>
                    <TableCell>{t("pharmacy.company")}</TableCell>
                    <TableCell>{t("pharmacy.purchase")}</TableCell>
                    <TableCell align="right">
                      {t("pharmacy.totalAmount")}
                    </TableCell>
                    <TableCell>{t("pharmacy.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length ? (
                    rows.map((r, i) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{(page - 1) * perPage + i + 1}</TableCell>
                        <TableCell>{r.date}</TableCell>
                        <TableCell>{r.company_display}</TableCell>
                        <TableCell>{r.purchase}</TableCell>
                        <TableCell align="right">{r.total_amount}</TableCell>
                        <TableCell>
                          <IconButton onClick={() => openDetail(r)}>
                            <Visibility />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(r.id)}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        {t("pharmacy.noRecords")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={2}
            >
              <FormControl size="small">
                <Select
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(+e.target.value);
                    setPage(1);
                  }}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Pagination
                count={Math.ceil(total / perPage)}
                page={page}
                onChange={(_, p) => setPage(p)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={open} onClose={closeDetail} fullWidth maxWidth="md">
        <DialogTitle>{t("pharmacy.returnDetails")}</DialogTitle>
        <DialogContent dividers>
          {selected ? (
            <>
              <Typography>
                <strong>{t("pharmacy.id")}:</strong> {selected.id}
              </Typography>
              <Typography>
                <strong>{t("pharmacy.date")}:</strong> {selected.date}
              </Typography>
              <Typography>
                <strong>{t("pharmacy.purchase")}:</strong>{" "}
                {selected.purchase_display}
              </Typography>
              <Typography>
                <strong>{t("pharmacy.totalAmount")}:</strong>{" "}
                {selected.total_amount}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6">{t("pharmacy.items")}</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>{t("pharmacy.medicine")}</TableCell>
                    <TableCell align="center">
                      {t("pharmacy.quantity")}
                    </TableCell>
                    <TableCell align="center">
                      {t("pharmacy.unitPrice")}
                    </TableCell>
                    <TableCell align="center">{t("pharmacy.total")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selected.items.map((it, i) => (
                    <TableRow key={it.id}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{it.medicine_display}</TableCell>
                      <TableCell align="center">{it.quantity}</TableCell>
                      <TableCell align="center">{it.purchase_price}</TableCell>
                      <TableCell align="center">
                        {it.quantity * it.purchase_price}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail} variant="contained">
            {t("pharmacy.close")}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog />
    </Box>
  );
};

export default ListReturnToCompany;
